<template>
  <a-drawer
    :visible="visible"
    :mask-closable="true"
    :destroy-on-close="true"
    placement="right"
    :closable="false"
    :width="480"
    :body-style="{
      padding: '0px',
      marginBottom: '53px'
    }"
    :mask-style="{
      backgroundColor: 'transparent'
    }"
    @update:visible="visible => $emit('update:visible', visible)"
  >
    <div class="container">
      <div class="review-title">
        {{ $t('okr.review.achievement') }}
      </div>
      <div class="tip">
        ({{ $t('okr.review.achievementPlaceholder') }})
      </div>
      <MentionTextarea
        v-model:value="result.content"
        :mentioned-user-ids="result.mentionedUserIds"
        :placeholder="$t('okr.review.placeholder')"
        :rows="3"
      />
      <div class="review-title">
        {{ $t('okr.review.problem') }}
      </div>
      <div class="tip">
        ({{ $t('okr.review.problemPlaceholder') }})
      </div>
      <MentionTextarea
        v-model:value="problem.content"
        :mentioned-user-ids="problem.mentionedUserIds"
        :placeholder="$t('okr.review.placeholder')"
        :rows="3"
      />
      <Upload
        ref="upload"
        class="upload"
        :button="{
          name: $t('okr.review.uploadButton'),
          type: 'text',
          size: 'small',
          style: {
            marginTop: '8px',
            backgroundColor: '#F8F9FF'
          }
        }"
        :before-upload="beforeUpload"
        @files-uploaded="onAttachmentsUploaded"
      />
      <FileList
        :can-edit="true"
        :files="reviewAttachments"
        :style="{ marginTop: '8px' }"
        @file-deleted="onAttachmentDeleted"
      />
    </div>
    <template #title>
      <div
        class="drawer-title"
      >
        <IconFont
          type="iconfupanzongjie"
          class="icon"
        />
        {{ review.reviewFlag + $t(`okr.review.title`) }}
      </div>
    </template>
    <div class="drawer-form-action">
      <a-button
        style="margin-right: 8px"
        @click="$emit('update:visible', false)"
      >
        {{ $t('shared.cancel') }}
      </a-button>
      <a-button
        type="primary"
        :loading="loading"
        @click="save"
      >
        {{ $t('shared.editDialog.ok') }}
      </a-button>
    </div>
  </a-drawer>
</template>
<script>
// import map from 'lodash/map'
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { message } from 'ant-design-vue'

import { MAX_FILE_SIZE, MAX_FILE_NUM } from '@/constants/file'
import { replaceHtml2Str, replaceStr2Html } from '@/utils/line-break'
import { newUpdateReview as updateReview, createReview } from '@/api/okr'
import MentionTextarea from '@/components/MentionTextarea'
import Upload from '@/components/Upload'
import FileList from '@/components/FileList'

const maxFileSizeMB = MAX_FILE_SIZE / 1024 / 1024

export default {
  components: {
    MentionTextarea,
    Upload,
    FileList
  },
  props: {
    objective: {
      type: Object,
      required: true
    },
    visible: {
      type: Boolean,
      required: true
    },
    review: {
      type: Object,
      default: () => ({})
    }
  },
  emits: [
    'update:visible',
    'review-saved',
    'attachments-saved'
  ],
  setup (props, { emit }) {
    const upload = ref(null)
    const { t } = useI18n()
    const result = ref({ content: '', mentionedUserIds: [] })
    const problem = ref({ content: '', mentionedUserIds: [] })
    // const hasChanged = ref(false)
    watch(() => props.review, (val) => {
      result.value = {
        content: props.review.progressAchievement
          ? replaceHtml2Str(props.review.progressAchievement)
          : '',
        mentionedUserIds: props.review.progressAchievementMentionUids
          ? props.review.progressAchievementMentionUids.split(',')
          : []
      }

      problem.value = {
        content: props.review.decisionIssue
          ? replaceHtml2Str(props.review.decisionIssue)
          : '',
        mentionedUserIds: props.review.decisionIssueMentionUids
          ? props.review.decisionIssueMentionUids.split(',')
          : []
      }
    })

    const reviewAttachments = computed(() => {
      return JSON.parse(props.review.reviewAttachments || '[]')
    })

    function beforeUpload (files) {
      if (files.length > 5) {
        message.warning(t('okr.review.uploadMaxNumWarning', { maxFileNum: MAX_FILE_NUM }))
        return false
      }

      let hasOverflowFile = false

      files.forEach(file => {
        if (file.size > MAX_FILE_SIZE) {
          hasOverflowFile = true
          message.warning(t('okr.review.uploadMaxSizeWarning', {
            filename: file.name,
            maxFileSize: maxFileSizeMB
          }))
        }
      })

      if (hasOverflowFile) {
        return false
      }

      return true
    }

    function onAttachmentsUploaded (newAttachments) {
      const attachments = [
        ...reviewAttachments.value,
        ...newAttachments.map(item => ({
          id: item.id,
          name: item.filename
        }))
      ]
      const reviewId = props.review.id
      const objectiveId = props.objective.id
      const params = {
        objective_id: ('id' in props.review) ? undefined : objectiveId,
        progress_achievement: replaceStr2Html(result.value.content),
        progress_achievement_mention_uids: result.value.mentionedUserIds.join(','),
        decision_issue: replaceStr2Html(problem.value.content),
        decision_issue_mention_uids: problem.value.mentionedUserIds.join(','),
        review_attachments: JSON.stringify(attachments),
        review_flag: props.review.reviewFlag
      }
      const p = reviewId ? updateReview(reviewId, params) : createReview(objectiveId, params)
      p.then(() => {
        upload.value.reset()
        emit('review-saved')
      })
    }

    function onAttachmentDeleted (attachmentId) {
      const attachments = reviewAttachments.value
        .filter(attachment => attachment.id !== attachmentId)
      updateReview(props.review.id, {
        review_attachments: JSON.stringify(attachments),
        review_flag: props.review.reviewFlag
      }).then(() => emit('review-saved'))
    }

    const loading = ref(false)
    function save () {
      loading.value = true
      const reviewId = props.review.id
      const objectiveId = props.objective.id
      const params = {
        objective_id: ('id' in props.review) ? undefined : objectiveId,
        progress_achievement: replaceStr2Html(result.value.content),
        progress_achievement_mention_uids: result.value.mentionedUserIds.join(','),
        decision_issue: replaceStr2Html(problem.value.content),
        decision_issue_mention_uids: problem.value.mentionedUserIds.join(','),
        review_flag: props.review.reviewFlag
      }
      const p = reviewId ? updateReview(reviewId, params) : createReview(objectiveId, params)
      p.then(() => {
        loading.value = false
        emit('update:visible', false)
        emit('review-saved')
        message.success(t('okr.review.saveSuccessMessage'))
      })
    }

    return {
      upload,
      result,
      problem,
      beforeUpload,
      onAttachmentsUploaded,
      onAttachmentDeleted,
      save,
      reviewAttachments,
      loading
    }
  }
}
</script>
<style lang="less" scoped>
.drawer-title {
  display: flex;
  // justify-content: space-between;
  align-items: center;
  line-height: 32px;
}
.container {
  padding: 0 16px;
}

.icon {
  margin-right: 8px;
}

.review-title {
  font-weight: bold;
  margin-top: 24px;
}

.upload {
  margin-top: 24px;
}

.tip {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.56);
  margin: 8px 0;
}
</style>
